import { PageContainer } from '@/components/PageContainer';
import { DeleteDialog } from '@/components/DeleteDialog';
import { trpc } from '@/lib/trpc';
import { Button } from '@televet/kibble-ui/Button';
import { Text } from '@televet/kibble-ui/Text';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Card } from '@televet/kibble-ui/Card';
import { Tag } from '@televet/kibble-ui/Tag';
import { Flex, Table, Box } from '@televet/kibble-ui/chakra';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/_authenticated/chats/')({
  component: RouteComponent,
  beforeLoad: () => {
    return {
      pageTitle: 'Agent Chats',
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [agentNameFilter, setAgentNameFilter] = useState('');
  const [clinicIdFilter, setClinicIdFilter] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<{
    id: string;
    summary: string | null;
  } | null>(null);

  const { data: conversationsData } = useQuery(
    trpc.agentConversation.list.queryOptions({
      page,
      limit: 10,
      where: {
        agentName: agentNameFilter || undefined,
        clinicId: clinicIdFilter || undefined,
      },
    })
  );

  const { mutate: deleteConversation, isPending: isDeleting } = useMutation(
    trpc.agentConversation.deleteOne.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [['agentConversation', 'list']],
        });
        setDeleteDialogOpen(false);
        setConversationToDelete(null);
      },
    })
  );

  const handleViewConversation = (chatId: string) => {
    navigate({ to: `/chats/${chatId}` });
  };

  const formatDate = (date: string | Date) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCreatedByDisplay = (createdByUser: any) => {
    if (!createdByUser) {
      return '-';
    }
    const fullName = [createdByUser.firstName, createdByUser.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (fullName) {
      return `${fullName} (${createdByUser.email})`;
    }
    return createdByUser.email || '-';
  };

  const formatTokens = (count: number) => {
    if (count === 0) return '-';
    return count.toLocaleString();
  };

  const formatCost = (cost: number) => {
    if (cost === 0) return '-';
    return `$${cost.toFixed(2)}`;
  };

  const handleDeleteClick = (conversation: {
    id: string;
    aiSummary: string | null;
  }) => {
    setConversationToDelete({
      id: conversation.id,
      summary: conversation.aiSummary,
    });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (conversationToDelete) {
      deleteConversation({ id: conversationToDelete.id });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setConversationToDelete(null);
  };

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={4}>
        <Flex gap={4} align="flex-end" wrap="wrap">
          <Flex direction="column" minWidth="200px" flex="1" gap={2}>
            <Text fontWeight="medium" fontSize="sm">
              Filter by Agent
            </Text>
            <TextInput
              placeholder="Search by agent name..."
              value={agentNameFilter}
              onChange={(e) => {
                setAgentNameFilter(e.target.value);
                setPage(1);
              }}
            />
          </Flex>
          <Flex direction="column" minWidth="200px" flex="1" gap={2}>
            <Text fontWeight="medium" fontSize="sm">
              Filter by Clinic ID
            </Text>
            <TextInput
              placeholder="Search by clinic ID..."
              value={clinicIdFilter}
              onChange={(e) => {
                setClinicIdFilter(e.target.value);
                setPage(1);
              }}
            />
          </Flex>
        </Flex>

        <Flex flex="1 1 auto" justify="center" align="flex-start">
          <Card
            flex="1 1 auto"
            direction="column"
            borderColor="border.default"
            rounded="lg"
            overflow="hidden"
          >
              <Box overflowX="auto" position="relative" width="100%">
                <Table.Root size="md" interactive width="100%" minWidth="800px">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader minWidth="200px" whiteSpace="nowrap">
                        Created By
                      </Table.ColumnHeader>
                      <Table.ColumnHeader minWidth="180px" whiteSpace="nowrap">
                        Clinic
                      </Table.ColumnHeader>
                      <Table.ColumnHeader minWidth="200px" whiteSpace="nowrap">
                        Summary
                      </Table.ColumnHeader>
                      <Table.ColumnHeader minWidth="120px" whiteSpace="nowrap">
                        Total Tokens
                      </Table.ColumnHeader>
                      <Table.ColumnHeader minWidth="100px" whiteSpace="nowrap">
                        Total Cost
                      </Table.ColumnHeader>
                      <Table.ColumnHeader minWidth="150px" whiteSpace="nowrap">
                        Updated
                      </Table.ColumnHeader>
                      <Table.ColumnHeader minWidth="120px" whiteSpace="nowrap">
                        Agent
                      </Table.ColumnHeader>
                      <Table.ColumnHeader
                        textAlign="end"
                        width="100px"
                        minWidth="100px"
                        position="sticky"
                        right="0"
                        bg="bg"
                        zIndex={1}
                        borderLeft="1px solid"
                        borderColor="border.default"
                        whiteSpace="nowrap"
                      >
                        Actions
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {conversationsData?.conversations.map((conversation) => {
                      const handleRowClick = () => {
                        handleViewConversation(conversation.id);
                      };
                      return (
                        <Table.Row
                          key={conversation.id}
                          onClick={handleRowClick}
                        >
                          <Table.Cell
                            fontSize="sm"
                            color="text.subtle"
                            whiteSpace="nowrap"
                          >
                            {getCreatedByDisplay(conversation.createdByUser)}
                          </Table.Cell>
                          <Table.Cell
                            fontSize="sm"
                            color="text.subtle"
                            whiteSpace="nowrap"
                          >
                            {conversation.clinicName || '-'}
                          </Table.Cell>
                          <Table.Cell fontSize="sm" whiteSpace="nowrap">
                            {conversation.aiSummary
                              ? conversation.aiSummary.substring(0, 40) + '...'
                              : '-'}
                          </Table.Cell>
                          <Table.Cell
                            fontSize="sm"
                            color="text.subtle"
                            whiteSpace="nowrap"
                          >
                            {formatTokens(conversation.totalTokens)}
                          </Table.Cell>
                          <Table.Cell
                            fontSize="sm"
                            color="text.subtle"
                            whiteSpace="nowrap"
                          >
                            {formatCost(conversation.totalCost)}
                          </Table.Cell>
                          <Table.Cell
                            fontSize="sm"
                            color="text.subtle"
                            whiteSpace="nowrap"
                          >
                            {formatDate(conversation.updatedAt)}
                          </Table.Cell>
                          <Table.Cell whiteSpace="nowrap">
                            <Tag label={conversation.currentAgent.name} variant="info" />
                          </Table.Cell>
                          <Table.Cell
                            textAlign="end"
                            position="sticky"
                            right="0"
                            bg="bg"
                            zIndex={1}
                            borderLeft="1px solid"
                            borderColor="border.default"
                          >
                            <Flex gap={2} justify="flex-end">
                              <Button
                                iconName="eyeOpen"
                                variant="ghostNeutral"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewConversation(conversation.id);
                                }}
                              />
                              <Button
                                iconName="trashcan"
                                variant="ghostDestructive"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(conversation);
                                }}
                              />
                            </Flex>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table.Root>
              </Box>
          </Card>
        </Flex>

        {conversationsData?.pagination?.total ? (
          <Flex
            justify="space-between"
            align="center"
            gap={4}
            wrap="wrap"
            px={2}
          >
            <Text fontSize="sm" variant="subtle">
              {conversationsData.pagination.total} chat
              {conversationsData.pagination.total !== 1 ? 's' : ''} total
            </Text>
            <Flex gap={3} align="center">
              <Button
                size="sm"
                disabled={!conversationsData.pagination.hasPrev}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Text fontSize="sm" variant="subtle" minWidth="fit-content">
                Page {conversationsData.pagination.page} of{' '}
                {conversationsData.pagination.totalPages}
              </Text>
              <Button
                size="sm"
                disabled={!conversationsData.pagination.hasNext}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </Flex>
          </Flex>
        ) : undefined}
      </Flex>

      <DeleteDialog
        isOpen={deleteDialogOpen}
        title="Delete Chat"
        description={
          conversationToDelete
            ? `Are you sure you want to delete this chat?${
                conversationToDelete.summary
                  ? ` This will permanently delete the chat "${conversationToDelete.summary.substring(0, 50)}${conversationToDelete.summary.length > 50 ? '...' : ''}" and all its messages.`
                  : ' This will permanently delete the chat and all its messages.'
              } This action cannot be undone.`
            : ''
        }
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isLoading={isDeleting}
        isDangerous={true}
      />
    </PageContainer>
  );
}
